<template>
  <div class="assistant-card">
    <div v-for="key in groupKeys" :key="key" class="part-group">
      <template v-if="items[key]">
        <div class="part-group__header">
          <span class="part-group__code">
            <span class="part-group__searched">Buscado:</span>
            {{ items[key].code }}
            <span v-if="items[key].multiple" class="part-group__hint">
              · marca las que necesites
            </span>
          </span>
          <!-- En los de varias cada pieza marcada lleva su cantidad -->
          <label v-if="!items[key].multiple" class="part-group__qty">
            Cant.
            <input
              type="number"
              min="1"
              inputmode="numeric"
              enterkeyhint="done"
              :value="items[key].quantity"
              @focus="selectAll"
              @change="$emit('quantity', key, $event.target.value)"
            />
          </label>
        </div>
        <p v-if="!items[key].options.length" class="part-group__empty">
          Sin resultados
        </p>
        <label
          v-for="option in items[key].options"
          :key="option.id"
          class="part-option"
          :class="{ 'is-selected': isSelected(key, option) }"
        >
          <input
            v-if="items[key].multiple"
            type="checkbox"
            :checked="isSelected(key, option)"
            @change="$emit('toggle', key, option.id)"
          />
          <input
            v-else
            type="radio"
            :name="`${cardId}-${key}`"
            :checked="isSelected(key, option)"
            @change="$emit('select', key, option.id)"
          />
          <img v-if="option.image" :src="option.image" alt="" />
          <span class="part-option__body">
            <span class="part-option__title">{{ option.title }}</span>
            <span class="part-option__meta">
              <NkrCodeLink :code="option.nkrCode" />
              <template v-if="option.brand"> · {{ option.brand }}</template>
            </span>
            <!-- Dónde coincidió lo buscado, resaltado. Las conversaciones
                 guardadas antes sólo tienen el nombre del campo -->
            <span
              v-for="match in option.matches"
              :key="matchLabel(match)"
              class="part-option__match"
            >
              {{ matchLabel(match) }}:
              <span v-if="match.values" v-html="match.values.join(', ')"></span>
            </span>
            <span
              v-if="option.fits !== null && option.fits !== undefined"
              class="part-option__fits"
              :class="{ 'is-out': !option.fits }"
            >
              {{
                option.fits
                  ? "✓ Le queda al vehículo"
                  : "No figura para el vehículo"
              }}
            </span>
          </span>
          <span class="part-option__numbers">
            <span>{{ option.price !== null ? money(option.price) : "—" }}</span>
            <span :class="{ 'is-out': !option.stock }">
              Stock {{ option.stock }}
            </span>
            <!-- Un input dentro del <label> no marca ni desmarca la pieza -->
            <input
              v-if="items[key].multiple && isSelected(key, option)"
              class="part-option__qty"
              type="number"
              min="1"
              inputmode="numeric"
              enterkeyhint="done"
              title="Cantidad"
              :value="items[key].selected[option.id]"
              @click.stop
              @focus="selectAll"
              @change="
                $emit('option-quantity', key, option.id, $event.target.value)
              "
            />
          </span>
        </label>
      </template>
    </div>
  </div>
</template>

<script>
import { money } from "../format.js";
import NkrCodeLink from "./NkrCodeLink.vue";

// Opciones de search_parts (una por código: son la misma pieza) y de
// search_parts_by_text (multiple: piezas distintas, se marcan varias, cada una
// con su cantidad). Lo elegido vive en la burbuja (`items`), así que una
// búsqueda posterior de lo mismo se ve aquí también.
export default {
  name: "PartOptionsCard",
  components: { NkrCodeLink },
  props: {
    cardId: { type: String, required: true },
    groupKeys: { type: Array, required: true },
    items: { type: Object, required: true }
  },
  methods: {
    money,
    // Al entrar a una cantidad queda todo seleccionado: se escribe la nueva
    // encima sin borrar la anterior. Después del focus, porque el clic (o el
    // toque) que lo enfocó pone el cursor y quitaría la selección
    selectAll(event) {
      const input = event.target;
      setTimeout(() => input.select());
    },
    isSelected(key, option) {
      const item = this.items[key];
      return item.multiple
        ? Boolean((item.selected || {})[option.id])
        : item.selectedId === option.id;
    },
    matchLabel(match) {
      return typeof match === "string" ? match : match.label;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./card.scss";

.part-group + .part-group {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef0f3;
}
.part-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.part-group__code {
  font-weight: 600;
}
.part-group__searched,
.part-group__hint {
  font-weight: 400;
  color: #6b7280;
}
.part-option__qty {
  width: 52px;
  margin-top: 2px;
  padding: 1px 4px;
  border: 1px solid #93c5fd;
  border-radius: 4px;
  text-align: right;
}
.part-option__match {
  font-size: 11px;
  color: #6b7280;
  // Lo buscado dentro del valor, como en part-search
  ::v-deep em {
    background: #fef08a;
    font-style: normal;
    font-weight: 700;
    color: #111827;
  }
}
.part-group__qty {
  font-size: 12px;
  color: #6b7280;
  input {
    width: 52px;
    margin-left: 4px;
    padding: 2px 4px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
  }
}
.part-group__empty {
  font-size: 12px;
  color: #b91c1c;
}
.part-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  &:hover {
    background: #f9fafb;
  }
  &.is-selected {
    border-color: #2563eb;
    background: #eff6ff;
  }
  img {
    width: 36px;
    height: 36px;
    object-fit: contain;
    flex-shrink: 0;
  }
}
.part-option__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.part-option__title {
  font-size: 13px;
  line-height: 1.3;
}
.part-option__meta {
  font-size: 11px;
  color: #6b7280;
}
.part-option__fits {
  font-size: 11px;
  color: #15803d;
  &.is-out {
    color: #b45309;
  }
}
.part-option__numbers {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 12px;
  white-space: nowrap;
  .is-out {
    color: #b91c1c;
  }
}
</style>
